import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { isNativeApp } from './capacitor';
import { getUserStorage, getLastUserId } from './user-storage';

// Debug logging for push notifications
function debugLog(event: string, data?: unknown): void {
  console.log(`[PushNotifications] ${event}`, data !== undefined ? data : '');
}

// Track whether the native PushNotifications plugin is functional.
// When Firebase isn't configured (no google-services.json), any call to the
// plugin crashes the native CapacitorPlugins thread. We test with a safe call
// first and disable all subsequent calls if it fails.
let pluginAvailable: boolean | null = null;

async function isPushPluginAvailable(): Promise<boolean> {
  if (pluginAvailable !== null) return pluginAvailable;
  if (!isNativeApp()) {
    pluginAvailable = false;
    return false;
  }
  try {
    // checkPermissions is the lightest call; if Firebase isn't initialized
    // this will throw before we try to register listeners.
    await PushNotifications.checkPermissions();
    pluginAvailable = true;
    return true;
  } catch (e) {
    debugLog('PushNotifications plugin unavailable (Firebase not configured?)', e);
    pluginAvailable = false;
    return false;
  }
}

// Get stored FCM token (user-scoped)
export function getStoredFcmToken(): string | null {
  const userId = getLastUserId();
  const userStorage = getUserStorage(userId);
  return userStorage.getFcmToken();
}

// Store FCM token (user-scoped)
function storeFcmToken(token: string): void {
  const userId = getLastUserId();
  const userStorage = getUserStorage(userId);
  userStorage.setFcmToken(token);
}

// Check if notifications are enabled in user settings (user-scoped)
export function areNotificationsEnabledInSettings(): boolean {
  try {
    const userId = getLastUserId();
    const userStorage = getUserStorage(userId);
    const stored = userStorage.getNotifications();
    if (stored) {
      const settings = JSON.parse(stored);
      const toggles = settings.toggles || {};
      return Object.values(toggles).some(v => v === true);
    }
    return true;
  } catch {
    return true;
  }
}

// Permission states from Capacitor
type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

// Check current permission status
export async function checkPermissionStatus(): Promise<PermissionStatus> {
  if (!await isPushPluginAvailable()) {
    debugLog('Push plugin not available, returning denied');
    return 'denied';
  }

  try {
    const result = await PushNotifications.checkPermissions();
    debugLog('Permission status', result.receive);
    return result.receive;
  } catch (e) {
    debugLog('Failed to check permissions', e);
    return 'denied';
  }
}

// Request notification permissions
export async function requestNotificationPermission(): Promise<'granted' | 'denied'> {
  if (!await isPushPluginAvailable()) {
    debugLog('Push plugin not available, cannot request permissions');
    return 'denied';
  }

  try {
    debugLog('Requesting notification permissions');
    const result = await PushNotifications.requestPermissions();
    debugLog('Permission request result', result.receive);
    
    if (result.receive === 'granted') {
      // Register for push notifications after permission granted
      await registerForPushNotifications();
    }
    
    return result.receive === 'granted' ? 'granted' : 'denied';
  } catch (e) {
    debugLog('Failed to request permissions', e);
    return 'denied';
  }
}

// Register for push notifications and get FCM token
export async function registerForPushNotifications(): Promise<string | null> {
  if (!await isPushPluginAvailable()) {
    debugLog('Push plugin not available, cannot register');
    return null;
  }

  try {
    debugLog('Registering for push notifications');
    await PushNotifications.register();
    
    // Token will be received via the 'registration' event listener
    // Return the stored token if available
    return getStoredFcmToken();
  } catch (e) {
    debugLog('Failed to register for push notifications', e);
    return null;
  }
}

// Callback types
type OnTokenReceived = (token: string) => void;
type OnNotificationReceived = (notification: PushNotificationSchema) => void;
type OnNotificationActionPerformed = (action: ActionPerformed) => void;

// Listener cleanup functions
let cleanupListeners: (() => void)[] = [];

// Initialize push notification listeners
export async function initPushNotificationListeners(
  onTokenReceived?: OnTokenReceived,
  onNotificationReceived?: OnNotificationReceived,
  onNotificationActionPerformed?: OnNotificationActionPerformed
): Promise<void> {
  if (!await isPushPluginAvailable()) {
    debugLog('Push plugin not available, skipping listener initialization');
    return;
  }

  debugLog('Initializing push notification listeners');

  // Clean up any existing listeners
  cleanupPushNotificationListeners();

  try {
    // Registration event - fires when device gets FCM token
    const registrationListener = await PushNotifications.addListener('registration', (token: Token) => {
      debugLog('FCM token received', token.value);
      storeFcmToken(token.value);
      onTokenReceived?.(token.value);
    });
    cleanupListeners.push(() => registrationListener.remove());

    // Registration error event
    const registrationErrorListener = await PushNotifications.addListener('registrationError', (error) => {
      debugLog('Registration error', error);
    });
    cleanupListeners.push(() => registrationErrorListener.remove());

    // Push notification received while app is in foreground
    const notificationReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      debugLog('Notification received in foreground', notification);
      onNotificationReceived?.(notification);
    });
    cleanupListeners.push(() => notificationReceivedListener.remove());

    // User tapped on a notification
    const notificationActionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      debugLog('Notification action performed', action);
      onNotificationActionPerformed?.(action);
    });
    cleanupListeners.push(() => notificationActionListener.remove());

    debugLog('Push notification listeners initialized');
  } catch (e) {
    debugLog('Failed to initialize push notification listeners', e);
  }
}

// Cleanup push notification listeners
export function cleanupPushNotificationListeners(): void {
  debugLog('Cleaning up push notification listeners');
  cleanupListeners.forEach(cleanup => cleanup());
  cleanupListeners = [];
}

// Full initialization flow - check permissions, request if needed, register
export async function initializePushNotifications(
  onTokenReceived?: OnTokenReceived,
  onNotificationReceived?: OnNotificationReceived,
  onNotificationActionPerformed?: OnNotificationActionPerformed
): Promise<{ permissionGranted: boolean; token: string | null }> {
  if (!await isPushPluginAvailable()) {
    return { permissionGranted: false, token: null };
  }

  // Initialize listeners first
  await initPushNotificationListeners(
    onTokenReceived,
    onNotificationReceived,
    onNotificationActionPerformed
  );

  // Check current permission status
  const status = await checkPermissionStatus();
  
  if (status === 'granted') {
    // Already have permission, register for notifications
    const token = await registerForPushNotifications();
    return { permissionGranted: true, token };
  } else if (status === 'prompt' || status === 'prompt-with-rationale') {
    // Need to request permission - this should be done via UI prompt
    // Don't auto-request here, let the UI handle it
    return { permissionGranted: false, token: null };
  } else {
    // Permission denied
    return { permissionGranted: false, token: null };
  }
}
